import { Hash, Plus, Trash2, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
import {
  useIvrFlows,
  usePromptUploads,
  useTtsFlows,
  useTtsTypes,
  useUserGroupAgents,
  useUserLanguages,
} from '../../api';
import { useAuthStore } from '@/lib/auth/store';
import { useUserDetails } from '@/features/users/api';
import { cn } from '@/lib/utils/cn';
import {
  MENU_WAIT_MAX,
  MENU_WAIT_MIN,
  RE_PROMPT_MAX,
  RE_PROMPT_MIN,
  templateFeatures,
  type PatchListRow,
  type TtsRow,
  type VoiceCampaignFormValues,
} from '../../types';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}

const DTMF_OPTIONS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map(
  (d) => ({ value: d, label: d }),
);

/** Legacy promptCategory values returned by `/api/obd/prompts/{userId}`. */
type PromptCategory =
  | 'welcome'
  | 'menu'
  | 'noinput'
  | 'wronginput'
  | 'thanks'
  | 'noagent';

/** Clamp a numeric-text input into [min, max]; reject anything else. */
function clampNumericText(raw: string, min: number, max: number): string {
  const stripped = raw.replace(/\D/g, '');
  if (!stripped) return '';
  const n = Number(stripped);
  if (n < min) return String(min);
  if (n > max) return String(max);
  return String(n);
}

export function StepContent({ values, setValues }: Props) {
  const prompts = usePromptUploads();
  const ivrs = useIvrFlows();
  const ttsFlows = useTtsFlows();
  const features = templateFeatures(values.templateId);

  /**
   * Filter prompts by category — each voice-file dropdown should only
   * surface files in its own category, matching the legacy.
   */
  const promptsByCategory = (category: PromptCategory) =>
    (prompts.data ?? [])
      .filter(
        (p) =>
          p.promptCategory === category &&
          (p.promptStatus === undefined ||
            Number(p.promptStatus) === 1 ||
            p.promptStatus === '1'),
      )
      .map((p) => ({
        value: String(p.promptId),
        label: p.fileName || p.promptName || `#${p.promptId}`,
      }));

  const PromptSelect = ({
    label,
    value,
    onChange,
    category,
    required = false,
  }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    category: PromptCategory;
    required?: boolean;
  }) => {
    const options = promptsByCategory(category);
    return (
      <div className="space-y-2">
        <Label>
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue
              placeholder={
                prompts.isLoading
                  ? 'Loading…'
                  : options.length === 0
                    ? `No ${category} files uploaded`
                    : 'Pick a voice file'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  /* ----- Continuous campaign — TTS flow only ----- */
  if (features.hasTtsFlow) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Continuous campaign</CardTitle>
          <CardDescription>
            Pick the TTS flow that drives this campaign.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="tts-flow">
              TTS flow
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={values.selectedTTSFlowId}
              onValueChange={(v) => setValues({ selectedTTSFlowId: v })}
            >
              <SelectTrigger id="tts-flow">
                <SelectValue
                  placeholder={ttsFlows.isLoading ? 'Loading…' : 'Pick a TTS flow'}
                />
              </SelectTrigger>
              <SelectContent>
                {(ttsFlows.data ?? []).map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.ttsFlowName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    );
  }

  /* ----- Custom IVR ----- */
  if (features.hasIvr) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custom IVR</CardTitle>
          <CardDescription>
            Pick the IVR flow you designed in the IVR builder.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="ivr">
              IVR flow
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={values.ivrId}
              onValueChange={(v) => setValues({ ivrId: v })}
            >
              <SelectTrigger id="ivr">
                <SelectValue
                  placeholder={ivrs.isLoading ? 'Loading…' : 'Pick an IVR'}
                />
              </SelectTrigger>
              <SelectContent>
                {(ivrs.data ?? []).map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.flowName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    );
  }

  const showVoiceFiles =
    features.requiredVoiceFiles.length > 0 ||
    features.optionalVoiceFiles.length > 0;

  return (
    <div className="space-y-4">
      {/* Voice Files (formerly "Prompts") */}
      {showVoiceFiles && (
        <Card>
          <CardHeader>
            <CardTitle>Voice Files</CardTitle>
            <CardDescription>
              Pick the pre-recorded audio files this campaign should play. Optional files
              only ship when their toggle is on.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Optional voice file toggles */}
            {features.optionalVoiceFiles.length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border border-border bg-muted/30 p-3">
                {features.optionalVoiceFiles.includes('welcome') && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={values.useWelcomeFile}
                      onCheckedChange={(v) =>
                        setValues({ useWelcomeFile: Boolean(v) })
                      }
                    />
                    Welcome file
                  </label>
                )}
                {features.optionalVoiceFiles.includes('noinput') && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={values.useNoinputFile}
                      onCheckedChange={(v) =>
                        setValues({ useNoinputFile: Boolean(v) })
                      }
                    />
                    No-input file
                  </label>
                )}
                {features.optionalVoiceFiles.includes('wronginput') && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={values.useWronginputFile}
                      onCheckedChange={(v) =>
                        setValues({ useWronginputFile: Boolean(v) })
                      }
                    />
                    Wrong-input file
                  </label>
                )}
                {features.optionalVoiceFiles.includes('thanks') && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={values.useThanksFile}
                      onCheckedChange={(v) =>
                        setValues({ useThanksFile: Boolean(v) })
                      }
                    />
                    Thanks file
                  </label>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {features.requiredVoiceFiles.includes('welcome') && (
                <PromptSelect
                  label="Welcome"
                  category="welcome"
                  required
                  value={values.welcomeId}
                  onChange={(v) => setValues({ welcomeId: v })}
                />
              )}
              {features.optionalVoiceFiles.includes('welcome') &&
                values.useWelcomeFile && (
                  <PromptSelect
                    label="Welcome"
                    category="welcome"
                    value={values.welcomeId}
                    onChange={(v) => setValues({ welcomeId: v })}
                  />
                )}

              {features.requiredVoiceFiles.includes('menu') && (
                <PromptSelect
                  label="Menu"
                  category="menu"
                  required
                  value={values.menuId}
                  onChange={(v) => setValues({ menuId: v })}
                />
              )}

              {features.optionalVoiceFiles.includes('noinput') &&
                values.useNoinputFile && (
                  <PromptSelect
                    label="No input"
                    category="noinput"
                    value={values.noinputId}
                    onChange={(v) => setValues({ noinputId: v })}
                  />
                )}
              {features.optionalVoiceFiles.includes('wronginput') &&
                values.useWronginputFile && (
                  <PromptSelect
                    label="Wrong input"
                    category="wronginput"
                    value={values.wronginputId}
                    onChange={(v) => setValues({ wronginputId: v })}
                  />
                )}
              {features.optionalVoiceFiles.includes('thanks') &&
                values.useThanksFile && (
                  <PromptSelect
                    label="Thanks"
                    category="thanks"
                    value={values.thanksId}
                    onChange={(v) => setValues({ thanksId: v })}
                  />
                )}

              {features.requiredVoiceFiles.includes('noAgent') && (
                <PromptSelect
                  label="No agent available"
                  category="noagent"
                  required
                  value={values.noAgentId}
                  onChange={(v) => setValues({ noAgentId: v })}
                />
              )}
            </div>

            {features.needsMenuWait && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="menu-wait">
                    Menu wait time (seconds)
                    <span className="ml-1 text-destructive">*</span>
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      · {MENU_WAIT_MIN}–{MENU_WAIT_MAX}
                    </span>
                  </Label>
                  <Input
                    id="menu-wait"
                    inputMode="numeric"
                    value={values.menuWaitTime}
                    onChange={(e) =>
                      setValues({
                        menuWaitTime: clampNumericText(
                          e.target.value,
                          MENU_WAIT_MIN,
                          MENU_WAIT_MAX,
                        ),
                      })
                    }
                    placeholder={`${MENU_WAIT_MIN}–${MENU_WAIT_MAX}`}
                  />
                  <p className="text-xs text-muted-foreground">
                    Only numbers between {MENU_WAIT_MIN} and {MENU_WAIT_MAX} are accepted.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="re-prompt">
                    Re-prompt count
                    <span className="ml-1 text-destructive">*</span>
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      · {RE_PROMPT_MIN}–{RE_PROMPT_MAX}
                    </span>
                  </Label>
                  <Input
                    id="re-prompt"
                    inputMode="numeric"
                    value={values.rePrompt}
                    onChange={(e) =>
                      setValues({
                        rePrompt: clampNumericText(
                          e.target.value,
                          RE_PROMPT_MIN,
                          RE_PROMPT_MAX,
                        ),
                      })
                    }
                    placeholder={`${RE_PROMPT_MIN}–${RE_PROMPT_MAX}`}
                  />
                  <p className="text-xs text-muted-foreground">
                    Only numbers between {RE_PROMPT_MIN} and {RE_PROMPT_MAX} are accepted.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* DTMF picker (templates 1, 8) */}
      {features.hasDtmfPicker && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1">
              DTMF
              <span className="text-destructive">*</span>
            </CardTitle>
            <CardDescription>
              Pick which DTMF digits should be accepted as valid input.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <MultiSelect
              options={DTMF_OPTIONS}
              value={values.dtmf}
              onChange={(v) => setValues({ dtmf: v })}
              placeholder="Pick accepted DTMF digits"
              searchPlaceholder="Filter…"
            />
            {values.dtmf.length > 0 && values.templateId !== '8' && (
              <div className="space-y-2">
                <Label htmlFor="repeat-menu">Repeat menu on DTMF</Label>
                <Select
                  value={values.repeatMenu || 'none'}
                  onValueChange={(v) =>
                    setValues({ repeatMenu: v === 'none' ? '' : v })
                  }
                >
                  <SelectTrigger id="repeat-menu">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {values.dtmf.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Call patch agent groups (templates 2, 9) */}
      {features.hasPatchList && (
        <PatchAgentsCard values={values} setValues={setValues} />
      )}

      {/* TTS settings (templates 7, 8, 9) */}
      {features.hasTts && (
        <TtsCard values={values} setValues={setValues} />
      )}
    </div>
  );
}

/**
 * Patch-agent picker — adds a row per (Agent Group × DTMF) pair, matching
 * the legacy /api/obd/group/agent/list/{userId} flow. DTMF digits are
 * one-shot: each digit can only be bound to one group at a time.
 */
function PatchAgentsCard({
  values,
  setValues,
}: {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}) {
  const groups = useUserGroupAgents();
  const [pickGroupId, setPickGroupId] = useState('');
  const [pickDtmf, setPickDtmf] = useState('');

  const groupOptions = (groups.data ?? []).map((g) => ({
    value: String(g.groupId),
    label: g.groupName,
  }));

  const takenDtmf = new Set(values.patchList.map((r) => r.agentDtmf));

  const handleAdd = () => {
    if (!pickGroupId || !pickDtmf) return;
    if (takenDtmf.has(pickDtmf)) return;
    const group = (groups.data ?? []).find(
      (g) => String(g.groupId) === pickGroupId,
    );
    if (!group) return;
    setValues({
      patchList: [
        ...values.patchList,
        {
          groupId: pickGroupId,
          groupName: group.groupName,
          agentDtmf: pickDtmf,
        },
      ],
    });
    setPickGroupId('');
    setPickDtmf('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Patch agents</CardTitle>
        <CardDescription>
          Bind an agent group to each DTMF digit. When the caller presses a digit, the
          call patches to an available agent in that group.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_auto] sm:items-end rounded-md border border-border p-3">
          <div className="space-y-1.5">
            <Label htmlFor="agent-group">
              Agent group
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select value={pickGroupId} onValueChange={setPickGroupId}>
              <SelectTrigger id="agent-group">
                <SelectValue
                  placeholder={
                    groups.isLoading
                      ? 'Loading…'
                      : groupOptions.length === 0
                        ? 'No agent groups configured'
                        : 'Pick an agent group'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {groupOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agent-dtmf">
              DTMF
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select value={pickDtmf} onValueChange={setPickDtmf}>
              <SelectTrigger id="agent-dtmf">
                <SelectValue placeholder="Digit" />
              </SelectTrigger>
              <SelectContent>
                {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
                  .filter((d) => !takenDtmf.has(d))
                  .map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!pickGroupId || !pickDtmf}
          >
            <Plus className="h-4 w-4" />
            Bind
          </Button>
        </div>

        <PatchKeypad
          patchList={values.patchList}
          onRemove={(dtmf) =>
            setValues({
              patchList: values.patchList.filter((r) => r.agentDtmf !== dtmf),
            })
          }
        />
      </CardContent>
    </Card>
  );
}

/**
 * Phone-keypad-style visualization of agent-group → DTMF bindings.
 * Layout follows a real phone keypad (1–9 in a 3×3 grid + 0 centered below).
 * Bound keys glow with the primary accent and show the bound group name;
 * unbound keys are muted placeholders.
 */
function PatchKeypad({
  patchList,
  onRemove,
}: {
  patchList: PatchListRow[];
  onRemove: (dtmf: string) => void;
}) {
  const byDtmf = new Map(patchList.map((r) => [r.agentDtmf, r] as const));
  const layout: Array<string | null> = [
    '1', '2', '3',
    '4', '5', '6',
    '7', '8', '9',
    null, '0', null,
  ];

  return (
    <div className="rounded-md border border-border bg-muted/20 p-2.5">
      <div className="mx-auto grid w-full max-w-[260px] grid-cols-3 gap-1.5">
        {layout.map((cell, i) => {
          if (cell === null) return <div key={`empty-${i}`} aria-hidden />;
          const bound = byDtmf.get(cell);
          return (
            <div
              key={cell}
              className={cn(
                'group relative flex aspect-square flex-col items-center justify-center rounded-md border p-1 text-center transition-all',
                bound
                  ? 'border-primary bg-primary/10'
                  : 'border-dashed border-border bg-card text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'text-base font-bold leading-none',
                  bound ? 'text-primary' : 'text-muted-foreground/50',
                )}
              >
                {cell}
              </span>
              {bound && (
                <span className="mt-0.5 max-w-full truncate text-[11px] font-medium leading-tight text-foreground">
                  {bound.groupName}
                </span>
              )}
              {bound && (
                <button
                  type="button"
                  onClick={() => onRemove(cell)}
                  aria-label={`Remove ${cell}`}
                  className="absolute -right-1 -top-1 rounded-full bg-destructive p-0.5 text-destructive-foreground opacity-0 transition-opacity hover:bg-destructive/90 group-hover:opacity-100"
                >
                  <Trash2 className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {patchList.length === 0 && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Bind at least one DTMF digit to an agent group.
        </p>
      )}
    </div>
  );
}

const MAX_TTS_VARS = 10;

/**
 * Faithful port of the legacy TTS row builder. The user picks a row
 * "Type" (prompt or variable). For `prompt` rows: pick a prompt id from
 * the appropriate prompt category (welcome only for T7; welcome+menu for
 * T8/T9). For `variable` rows: pick a 1..N variable index (capped at the
 * user's `totalVariable` from their profile) plus a typed format from
 * `/api/obd/tts/type/list`. Already-used variable indices are filtered out.
 */
function TtsCard({
  values,
  setValues,
}: {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}) {
  const userId = useAuthStore((s) => s.userId);
  const langs = useUserLanguages();
  const prompts = usePromptUploads();
  const ttsTypes = useTtsTypes();
  const profile = useUserDetails(userId);

  const [pickType, setPickType] = useState<'' | 'prompt' | 'variable'>('');
  const [pickValue, setPickValue] = useState('');
  const [pickVariableType, setPickVariableType] = useState('');

  // Which prompt categories are valid for the current template.
  const allowedCategories =
    values.templateId === '7' ? ['welcome'] : ['welcome', 'menu'];

  const promptOptions = (prompts.data ?? [])
    .filter(
      (p) =>
        (!p.promptCategory || allowedCategories.includes(p.promptCategory)) &&
        (p.promptStatus === undefined ||
          Number(p.promptStatus) === 1 ||
          p.promptStatus === '1'),
    )
    .map((p) => ({
      value: String(p.promptId),
      label: p.fileName || p.promptName || `#${p.promptId}`,
    }));

  const promptLabelFor = (id: string): string =>
    promptOptions.find((o) => o.value === id)?.label ?? `#${id}`;

  // Variables must be picked in ascending order (1, 2, 3, …) per the
  // legacy "Please Select Lower Variable Value First" guard, so the only
  // selectable next value is the smallest unused. Cap by user.totalVariable.
  const maxVars = Math.min(
    Number(profile.data?.totalVariable ?? MAX_TTS_VARS) || MAX_TTS_VARS,
    MAX_TTS_VARS,
  );
  const usedVariableValues = new Set(
    values.ttsRows.filter((r) => r.ttsType === 'variable').map((r) => r.value),
  );
  const nextVariable = (() => {
    for (let i = 1; i <= maxVars; i++) {
      if (!usedVariableValues.has(String(i))) return String(i);
    }
    return '';
  })();
  const availableVariables = nextVariable ? [nextVariable] : [];

  const handleAdd = () => {
    if (!pickType) return;
    if (pickType === 'prompt') {
      if (!pickValue) return;
      setValues({
        ttsRows: [
          ...values.ttsRows,
          { ttsType: 'prompt', value: pickValue, variableType: '' },
        ],
      });
    } else {
      if (!pickValue || !pickVariableType) return;
      setValues({
        ttsRows: [
          ...values.ttsRows,
          { ttsType: 'variable', value: pickValue, variableType: pickVariableType },
        ],
      });
    }
    setPickType('');
    setPickValue('');
    setPickVariableType('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>TTS</CardTitle>
        <CardDescription>
          Pick a language and voice gender, then build the TTS sequence one row at a time.
          Each row is either a static prompt or a dynamic variable.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="lang">
              Language
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={values.language}
              onValueChange={(v) => setValues({ language: v })}
            >
              <SelectTrigger id="lang">
                <SelectValue
                  placeholder={langs.isLoading ? 'Loading…' : 'Pick a language'}
                />
              </SelectTrigger>
              <SelectContent>
                {(langs.data ?? []).map((l, i) => (
                  <SelectItem key={`${l.language}-${i}`} value={l.language}>
                    {l.language}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender">
              Voice gender
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={values.gender}
              onValueChange={(v) => setValues({ gender: v })}
            >
              <SelectTrigger id="gender">
                <SelectValue placeholder="Pick a gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3 rounded-md border border-border p-3">
          <Label>Add a TTS row</Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="tts-type">Type</Label>
              <Select
                value={pickType}
                onValueChange={(v) => {
                  const next = v as 'prompt' | 'variable';
                  setPickType(next);
                  // Variables are forced sequential — auto-pick the next one
                  // so the user only has to choose its variable type.
                  setPickValue(next === 'variable' ? nextVariable : '');
                  setPickVariableType('');
                }}
              >
                <SelectTrigger id="tts-type">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prompt">Prompt</SelectItem>
                  <SelectItem value="variable">Variable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tts-value">
                {pickType === 'variable' ? 'Variable #' : 'Prompt'}
              </Label>
              {pickType === 'variable' ? (
                <Select value={pickValue} onValueChange={setPickValue}>
                  <SelectTrigger id="tts-value">
                    <SelectValue
                      placeholder={
                        availableVariables.length === 0
                          ? 'All variables used'
                          : 'Select variable'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableVariables.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={pickValue}
                  onValueChange={setPickValue}
                  disabled={!pickType}
                >
                  <SelectTrigger id="tts-value">
                    <SelectValue
                      placeholder={
                        !pickType
                          ? 'Pick a type first'
                          : prompts.isLoading
                            ? 'Loading…'
                            : promptOptions.length === 0
                              ? 'No matching prompts'
                              : 'Select prompt'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {promptOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tts-vartype">Variable type</Label>
              <Select
                value={pickVariableType}
                onValueChange={setPickVariableType}
                disabled={pickType !== 'variable'}
              >
                <SelectTrigger id="tts-vartype">
                  <SelectValue
                    placeholder={
                      pickType !== 'variable'
                        ? 'Variable rows only'
                        : ttsTypes.isLoading
                          ? 'Loading…'
                          : 'Select type'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(ttsTypes.data ?? []).map((t, i) => (
                    <SelectItem key={`${t.tts_type}-${i}`} value={t.tts_type}>
                      {t.tts_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              onClick={handleAdd}
              disabled={
                !pickType ||
                !pickValue ||
                (pickType === 'variable' && !pickVariableType)
              }
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>
            Playback sequence
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <TtsSequence
            rows={values.ttsRows}
            promptLabelFor={promptLabelFor}
            onRemove={(idx) =>
              setValues({
                ttsRows: values.ttsRows.filter((_, i) => i !== idx),
              })
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Vertical "sequence" view of TTS rows. Each row is a numbered card showing
 * what will be spoken at that step. Prompt rows show a speaker icon + file
 * name; Variable rows show the `{N}` token with its type badge. The line
 * connector between cards reinforces playback order.
 */
function TtsSequence({
  rows,
  promptLabelFor,
  onRemove,
}: {
  rows: TtsRow[];
  promptLabelFor: (id: string) => string;
  onRemove: (idx: number) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 py-8 text-center">
        <Volume2 className="mx-auto h-6 w-6 text-muted-foreground/50" />
        <p className="mt-2 text-sm text-muted-foreground">
          No TTS steps yet — add a prompt or variable above.
        </p>
      </div>
    );
  }
  return (
    <ol className="space-y-2">
      {rows.map((row, i) => {
        const isPrompt = row.ttsType === 'prompt';
        const Icon = isPrompt ? Volume2 : Hash;
        return (
          <li key={`${row.ttsType}-${row.value}-${i}`} className="relative">
            {/* Vertical connector to the next row */}
            {i < rows.length - 1 && (
              <span
                aria-hidden
                className="absolute left-[19px] top-[44px] h-[calc(100%-32px)] w-px bg-border"
              />
            )}
            <div
              className={cn(
                'relative flex items-center gap-3 rounded-lg border p-3 transition-colors',
                isPrompt
                  ? 'border-sky-200 bg-sky-50/60 dark:border-sky-900/40 dark:bg-sky-950/30'
                  : 'border-violet-200 bg-violet-50/60 dark:border-violet-900/40 dark:bg-violet-950/30',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold',
                  isPrompt
                    ? 'bg-sky-500 text-white'
                    : 'bg-violet-500 text-white',
                )}
              >
                {i + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    isPrompt
                      ? 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100'
                      : 'bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100',
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {isPrompt ? 'Prompt' : 'Variable'}
                </span>
                <span className="truncate text-sm font-medium text-foreground">
                  {isPrompt ? promptLabelFor(row.value) : `{${row.value}}`}
                </span>
                {!isPrompt && row.variableType && (
                  <span className="rounded border border-violet-300 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-violet-700 dark:border-violet-700 dark:bg-violet-950 dark:text-violet-200">
                    {row.variableType}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => onRemove(i)}
                aria-label="Remove row"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
